/**
 * Theme resolution and application.
 *
 * Three settings, two appearances: "system" follows the OS and is the default,
 * so a first-time visitor gets whichever they already prefer. Only "light" and
 * "dark" ever reach the DOM, as `data-theme` on <html>.
 */

export type ThemeSetting = 'light' | 'dark' | 'system';
export type Appearance = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'uptimer_theme';

const SETTINGS: ThemeSetting[] = ['light', 'dark', 'system'];

export function isThemeSetting(value: unknown): value is ThemeSetting {
  return typeof value === 'string' && (SETTINGS as string[]).includes(value);
}

/** Reads the saved setting, falling back to "system" for anything unusable. */
export function readStoredTheme(): ThemeSetting {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeSetting(stored) ? stored : 'system';
  } catch {
    // localStorage throws in private modes and when storage is blocked.
    // A theme preference is never worth breaking the app over.
    return 'system';
  }
}

export function storeTheme(setting: ThemeSetting): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, setting);
  } catch {
    // Preference simply will not persist; the session still honours it.
  }
}

export function prefersDark(): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Turns a setting into the appearance actually rendered. */
export function resolveAppearance(setting: ThemeSetting): Appearance {
  if (setting === 'system') {
    return prefersDark() ? 'dark' : 'light';
  }
  return setting;
}

/**
 * Applies an appearance to the document. `color-scheme` is set alongside
 * `data-theme` so form controls, scrollbars and the like follow too.
 */
export function applyAppearance(appearance: Appearance): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', appearance);
  root.style.colorScheme = appearance;
}

/**
 * Resolves a CSS custom property to a concrete value. Needed for anything
 * that cannot consume `var(--x)` — SVG presentation attributes, as used by
 * the charts, do not resolve custom properties.
 */
export function readToken(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}
